import React, { useCallback, useState } from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import { getNextgenSettingsWorkflowUrl } from '@jira/project-settings__get-settings-url/src/getClassicIssueLayoutSettingsUrl.tsx';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { Label, HelperMessage } from '@atlaskit/form';
import WarningIcon from '@atlaskit/icon/core/status-warning';
import { token } from '@atlaskit/tokens';

import { Skeleton } from '@atlassian/jira-common-components-skeleton/src/styled.tsx';
import type { ProjectType } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { FieldContainer } from '@atlassian/jira-issue-create-commons/src/common/ui/fields/styled.tsx';
import type { StatusTransition } from '@atlassian/jira-issue-field-status/src/common/types.tsx';
import { StatusView } from '@atlassian/jira-issue-field-status/src/ui/status-view/index.tsx';
import { GIC_VIEW_SOURCE } from '@atlassian/jira-status-config-common/src/types.tsx';
import { useIssueType } from '@atlassian/jira-issue-create-form-context/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { JiraIssueCreateErrorBoundary } from '@atlassian/jira-issue-create-analytics-common/src/ui/jira-issue-create-error-boundary/index.tsx';
import { MANAGE_WORKFLOW_ACTION_SUBJECT_ID } from '../../../../common/constants/analytics.tsx';
import { useOpenManageFieldTab } from '../../../../controllers/use-open-manage-field-tab/useOpenManageFieldTab.tsx';
import { ManageFieldButton } from './manage-field-button/ManageFieldButton.tsx';
import messages from './messages.tsx';

export type Props = {
	isLoading: boolean;
	error?: Error;
	transitions: StatusTransition[];
	value: StatusTransition | null;
	onChange?: (
		transition: StatusTransition,
		analyticsEvent: UIAnalyticsEvent,
	) => void | Promise<void>;
	isMini: boolean;
	canConfigureStatuses?: boolean;
	issueTypeId: string | undefined;
	projectKey: string;
	projectId: string;
	projectType: ProjectType | null;
	onStatusCreatedOrUpdated?: () => Promise<void>;
};

const statusFieldMargin = 18;
const HELPER_TEXT_ID = 'issue-field-status.status-view.select-status';

const StatusField = ({ onStatusCreatedOrUpdated, ...props }: Props) => {
	const { formatMessage } = useIntl();

	const [hasCreatedStatus, setHasCreatedStatus] = useState(false);
	const onStatusCreatedOrUpdatedCallback = useCallback(async () => {
		await onStatusCreatedOrUpdated?.();
		setHasCreatedStatus(true);
	}, [onStatusCreatedOrUpdated]);

	const defaultProps = {
		isLoading: false,
		footer: undefined,
		fieldId: 'gic-status-field',
	};

	const isConfigureStatusAvailable = props.canConfigureStatuses;

	const isManageWorkflowAvailable =
		isConfigureStatusAvailable && props.projectType && props.issueTypeId;

	const getEnabledTransitions = (transitions: StatusTransition[]) =>
		transitions.filter((transition) => transition.isDisabled === false);

	const nextgenWorkflowUrl =
		(props.projectType &&
			props.issueTypeId &&
			getNextgenSettingsWorkflowUrl(props.projectType, props.projectKey, props.issueTypeId)) ||
		'';

	const openManageWorkflow = useOpenManageFieldTab({
		source: MANAGE_WORKFLOW_ACTION_SUBJECT_ID,
		url: nextgenWorkflowUrl,
	});

	const onOpenManageWorkflow = isConfigureStatusAvailable ? openManageWorkflow : noop;

	// used as footer with popup select component
	const manageWorkFlowButton = ManageFieldButton({
		url: nextgenWorkflowUrl,
		label: formatMessage(messages.manageWorkflowFooter),
		source: MANAGE_WORKFLOW_ACTION_SUBJECT_ID,
	});

	const getFieldContent = () => {
		if (props.isLoading) return <FieldSkeleton isMini={props.isMini} aria-busy />;

		if (!props.value?.to) {
			// error state
			return <FieldErrorSkeleton isMini={props.isMini} />;
		}

		if (props.error) return <FieldErrorSkeleton isMini={props.isMini} />;

		return (
			<StatusView
				{...defaultProps}
				appearance="button"
				value={props.value?.to}
				selectedTransition={props.value}
				transitions={getEnabledTransitions(props.transitions)}
				forceNoLabels
				isDefaultSelect={props.isMini}
				onChange={props.onChange}
				customNoOptionsMessage={formatMessage(messages.noEnabledTransitionsMessage)}
				shouldFitContainer={false}
				showConfigureStatusButtons={props.canConfigureStatuses}
				configureStatusDialogType={props.isMini ? 'modal' : 'popup'}
				configureStatusPopupPlacement="bottom-start"
				projectId={props.projectId}
				issueTypeId={props.issueTypeId}
				projectKey={props.projectKey}
				projectType={props.projectType}
				onStatusCreatedOrUpdated={onStatusCreatedOrUpdatedCallback}
				footer={isManageWorkflowAvailable ? manageWorkFlowButton : null}
				onOpenManageWorkflow={onOpenManageWorkflow}
				configureStatusSource={GIC_VIEW_SOURCE}
				autofocus={hasCreatedStatus}
				helperTextId={HELPER_TEXT_ID}
			/>
		);
	};

	const [selectedIssueType] = useIssueType();
	const issueType = selectedIssueType?.label;
	const getMessage = () => {
		if (expVal('jira-itr-work-type-exp-1', 'isEnabled', false)) {
			return messages.warningMessageIssueTerminologyRefreshM4;
		}
		return expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
			? messages.warningMessageIssueTermRefresh
			: messages.warningMessage;
	};

	const getHelpOrWarningText = () => {
		if (props.error) {
			return (
				<HelperMessage>
					<WarningIcon
						color={token('color.icon.warning')}
						label={formatMessage(messages.warningIconLabel)}
					/>
					{formatMessage(getMessage(), { issueType })}
				</HelperMessage>
			);
		}
		if (props.isMini) {
			return null;
		}

		return <HelperText id={HELPER_TEXT_ID}>{formatMessage(messages.helperText)}</HelperText>;
	};
	if (fg('status_field_gic_error_boundary')) {
		return (
			<JiraIssueCreateErrorBoundary
				id="statusField"
				packageName="jiraIssueCreateModal"
				sendToPrivacyUnsafeSplunk
			>
				<FieldContainer marginTop={statusFieldMargin}>
					<Label htmlFor={defaultProps.fieldId} id="gic-status-field-label">
						<LabelFlex>{formatMessage(messages.statusFieldLabel)}</LabelFlex>
					</Label>
					{getFieldContent()}
					{getHelpOrWarningText()}
				</FieldContainer>
			</JiraIssueCreateErrorBoundary>
		);
	}

	return (
		<FieldContainer marginTop={statusFieldMargin}>
			<Label htmlFor={defaultProps.fieldId} id="gic-status-field-label">
				<LabelFlex>{formatMessage(messages.statusFieldLabel)}</LabelFlex>
			</Label>
			{getFieldContent()}
			{getHelpOrWarningText()}
		</FieldContainer>
	);
};

export default StatusField;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HelperText = styled.div({
	color: token('color.text.subtlest'),
	font: token('font.body.small'),
	marginTop: token('space.075'),
	marginBottom: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldSkeleton = styled(Skeleton)<{ isMini?: boolean }>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isMini }) =>
		isMini
			? {
					height: '40px',
					width: '100%',
				}
			: {
					height: '32px',
					width: '120px',
				},

	{ marginBottom: token('space.075') },
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldErrorSkeleton = styled.div<{ isMini?: boolean }>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isMini }) =>
		isMini
			? {
					height: '40px',
					width: '100%',
				}
			: {
					height: '32px',
					width: '120px',
				},
	{
		marginBottom: token('space.075'),

		backgroundColor: token('color.skeleton'),
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LabelFlex = styled.div({
	display: 'flex',
	alignItems: 'flex-start',
});
